/**
 * MarketingLayout · v2.6.9 batch BQ (Carrgo-inspired)
 * ====================================================
 * Public-site wrapper for Home / About / Our Services / Our Vehicles /
 * Taxi Rates / Contact Us. Yellow-black-white theme, 100% width with a
 * 75% centered content frame, mobile-first responsive.
 *
 * Header layout (Carrgo-style):
 *   [Logo]                [utility links: Driver Dashboard · Dispatch · Book a Ride]
 *   ────────────────────────────────────────────────────────────────────────────────
 *   [Main nav: Home · About · Our Services · Our Vehicles · Taxi Rates · Contact Us]
 *
 * Footer:
 *   [Logo + tagline]  [Quick Links]  [Contact]  [Social]
 *   ──────────────────────────────────────────────────────
 *   Copyright    ·    Payment method logos
 */
import { Link, NavLink } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu as MenuIcon, X as XIcon, Phone, Mail, MapPin, ChevronDown, Clock } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";
import usePortalHours from "@/hooks/usePortalHours";
import useMobileRedirect from "@/hooks/useMobileRedirect";
import PaymentLogos from "@/components/PaymentLogos";
import ChatbotWidget from "@/components/ChatbotWidget";
import KatieChatWidget from "@/components/customer/KatieChatWidget";
import InstallAppBanner from "@/components/InstallAppBanner";
import { brandIconFor, normalizeSocialUrl } from "@/components/marketing/BrandSocialIcons";
import api from "@/lib/api";

// v2.6.9 batch CF · Nav items are now declared inside the component so
// we can dynamically attach a "children" array for the Tourism dropdown
// (auto-built from CMS cities) and the About dropdown (Leadership +
// Drivers nested links). The static list below sets the static parts.
// v2.7.58+E36 · Nav trimmed per operator request — Vehicles/Weather/Tourism
// hidden from the visual menu (routes remain reachable via direct URL) and
// Delivery promoted to sit immediately before About.
const STATIC_NAV = [
  { to: "/",                 label: "HOME" },
  { to: "/delivery",         label: "DELIVERY" },   // v2.7.57+E34 · DELIVERY SYSTEM Section 1
  // v2.7.65+E134 · Rebranded "VOUCHER PROGRAM" → "CCVAP PROGRAM" (Community
  // Care Voucher Assistance Program). Now a hoverable dropdown with two
  // children: the public application page + the rider MY VOUCHERS portal.
  { to: "/voucher-program",  label: "CCVAP PROGRAM", hasDropdown: "ccvap" },
  { to: "/about",            label: "ABOUT", hasDropdown: "about" },
  { to: "/services",         label: "OUR SERVICES" },
  { to: "/loyalty",          label: "LOYALTY" },  // v2.7.65+E257 · Customer Loyalty Program
  { to: "/rates",            label: "TAXI RATES", hasDropdown: "rates" },
  { to: "/drive-for-us",     label: "DRIVE FOR US" },  // v2.7.65+E80 · was "DRIVE WITH US" — renamed per hiring team
  { to: "/contact",          label: "CONTACT US" },
];

// v2.7.65+E254 · Order fixed per operator: Book a Ride, Driver Dashboard,
// Dispatch. "Book a Ride" points to the restored rider portal (/ride/login).
const UTILITY_LINKS = [
  { to: "/ride/login",  label: "Book a Ride",      testid: "util-book-ride",  accent: "black-on-yellow" },
  { to: "/login",       label: "Driver Dashboard", testid: "util-driver",     accent: "amber" },
  { to: "/admin/login", label: "Dispatch",         testid: "util-dispatch",   accent: "white" },
];

const UTIL_CLASS = {
  amber: "text-amber-300 hover:text-amber-200",
  white: "text-zinc-200 hover:text-amber-300",
  "black-on-yellow": "bg-amber-400 text-zinc-950 hover:bg-amber-300 font-extrabold rounded px-3 py-1",
};

export default function MarketingLayout({ children, autoAssets = null }) {
  useMobileRedirect();
  const { cfg } = useSiteConfig();
  const [scannedAssets, setScannedAssets] = useState(autoAssets || {});
  // v2.6.9 batch BX · When the parent page doesn't pass autoAssets in (most
  // marketing routes don't), fetch from the scanner directly so the NAV
  // background + footer Bison AI logo still pick up dropped-in files.
  useEffect(() => {
    if (autoAssets) { setScannedAssets(autoAssets); return undefined; }
    let cancelled = false;
    api.get("/site/city-assets")
      .then((r) => { if (!cancelled) setScannedAssets(r.data?.assets || {}); })
      .catch(() => { if (!cancelled) setScannedAssets({}); });
    return () => { cancelled = true; };
  }, [autoAssets]);
  const g = cfg?.global || {};
  const [mobileNav, setMobileNav] = useState(false);
  const hoursInfo = usePortalHours();  // v2.8+E306 · footer Hours of Operation
  // v2.8+E307 · Always surface the official Facebook page in the footer,
  // even before an admin sets social_links in the CMS.
  const socialLinks = { ...(g.social_links || {}) };
  if (!socialLinks.facebook) socialLinks.facebook = "https://www.facebook.com/bullertaxiservices";

  const logo = g.logo_url || "/buller-taxi-crest.png";
  const phone = g.phone || "(901) 459-9448";
  // v2.6.9 batch CE · CMS upload WINS over the auto-scanner; scanner is
  // only a fallback when admin hasn't uploaded an asset.
  const navBgUrl = g.nav_background_url || scannedAssets.nav;
  const bisonAiLogoUrl = g.bison_ai_logo_url || scannedAssets.bison_ai;
  // v2.6.9 batch CF · TAXICOMMANDER + TAXI COMMANDER GPS logos for the
  // footer co-branding strip. CMS-driven; null hides individually.
  const taxiCommanderUrl = g.taxi_commander_logo_url;
  const taxiCommanderGpsUrl = g.taxi_commander_gps_logo_url;
  const hasAnyBrandLogo = !!(bisonAiLogoUrl || taxiCommanderUrl || taxiCommanderGpsUrl);
  // v2.6.9 batch BY · Checker pattern overlay extracted from wallpaper.png.
  // Falls back to the bundled nav-checker-pattern.png (a programmatically
  // cropped strip from the wallpaper) so the pattern is visible even before
  // an admin uploads a custom wallpaper asset.
  const checkerPatternUrl = scannedAssets.wallpaper || "/nav-checker-pattern.png";

  // v2.6.9 batch CA · NAV theme: leather-texture tile from the MENU asset.
  // Yellow solid bg from batch BZ is gone. The leather tile renders at its
  // natural size with `background-repeat: repeat` so it never stretches or
  // pixelates across viewports. A near-black overlay at ~78% maintains AAA
  // contrast for the nav text without flattening the leather grain.
  const navBgStyle = navBgUrl
    ? {
        backgroundImage: `linear-gradient(rgba(10,10,10,0.62), rgba(10,10,10,0.74)), url("${navBgUrl}")`,
        backgroundSize: "auto",       // ← never stretch
        backgroundRepeat: "repeat",    // ← tile cleanly
        backgroundPosition: "top left",
        backgroundColor: "#1a1a1a",   // fallback while image loads
      }
    : { backgroundColor: "#1a1a1a" };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="marketing-layout">
      {/* v2.7.0 batch CZ · PWA Install banner — sits ABOVE the sticky nav
          so mobile visitors see the install CTA the second they land.
          Self-hides if already installed / dismissed in last 7 days. */}
      <InstallAppBanner/>

      {/* ─────────────────── HEADER ─────────────────── */}
      <header
        className="sticky top-0 z-40 border-b border-amber-400/40 shadow-lg shadow-black/40"
        style={navBgStyle}
        data-testid="marketing-header"
      >
        {/* Utility top bar — translucent dark over the leather. */}
        <div className="bg-black/45 border-b border-amber-400/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-5 py-1.5 flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-3 text-amber-200 font-semibold">
              <a href={`tel:${phone.replace(/\D/g, "")}`} className="flex items-center gap-1 hover:text-amber-300" data-testid="util-phone">
                <Phone className="h-3 w-3"/>{phone}
              </a>
              <span className="hidden sm:flex items-center gap-1">
                <Mail className="h-3 w-3"/>{g.email || "support@bullertaxi.com"}
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              {UTILITY_LINKS.map((u) => (
                <Link
                  key={u.to}
                  to={u.to}
                  data-testid={u.testid}
                  className="text-[11px] uppercase tracking-wider transition-colors text-amber-300 hover:text-amber-200 font-bold"
                >
                  {u.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Main bar: logo + nav · v2.6.9 batch CA · Logo enlarged to 84 px
            (was 68 px in BZ → +24 % for "visibly larger"). Same negative-
            margin trick keeps the nav container height steady. */}
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="marketing-logo">
            <span className="block h-12 w-14 relative flex-shrink-0">
              <img
                src={logo}
                alt={g.site_name || "Buller Taxi"}
                className="absolute top-1/2 left-1/2 object-contain pointer-events-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
                style={{ width: "84px", height: "84px", transform: "translate(-50%, -50%)" }}
              />
            </span>
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-black leading-none">{g.site_name?.split(" ")[0] || "BULLER"}</div>
              <div className="text-base font-extrabold text-zinc-100 leading-tight">{g.site_name?.split(" ").slice(1).join(" ") || "Taxi Service"}</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="marketing-nav">
            {STATIC_NAV.map((n) => {
              if (n.hasDropdown === "tourism") {
                const tourismCities = (cfg?.tourism?.cities || []).filter((c) => c.enabled !== false);
                return (
                  <NavDropdown
                    key={n.to}
                    parent={n}
                    cfgTitle={cfg?.tourism?.title}
                    items={tourismCities.map((c) => ({ to: `/tourism/${c.slug}`, label: (c.name || "").toUpperCase() }))}
                  />
                );
              }
              if (n.hasDropdown === "about") {
                const items = [];
                // v2.7.65+E239 · "Our Team" replaces the old Leadership +
                // standalone Drivers entries under About.
                items.push({ to: "/our-team", label: "OUR TEAM" });
                items.push({ to: "/bison-taxi-commander", label: "BISON TAXI COMMANDER" });
                return (
                  <NavDropdown
                    key={n.to}
                    parent={n}
                    items={items}
                  />
                );
              }
              // v2.7.65+E134 · CCVAP Program dropdown — Info & Application +
              // MY VOUCHERS rider portal.
              if (n.hasDropdown === "ccvap") {
                return (
                  <NavDropdown
                    key={n.to}
                    parent={n}
                    items={[
                      { to: "/voucher-program", label: "CCVAP PROGRAM INFO & APPLICATION" },
                      { to: "/my-vouchers",     label: "MY VOUCHERS" },
                    ]}
                  />
                );
              }
              // v2.7.65+E238 · Taxi Rates dropdown with the Group Rides page.
              if (n.hasDropdown === "rates") {
                return (
                  <NavDropdown
                    key={n.to}
                    parent={n}
                    items={[{ to: "/group-rides", label: "GROUP RIDES" }]}
                  />
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-extrabold uppercase tracking-wide transition-colors rounded ${
                      isActive ? "bg-amber-400 text-zinc-950" : "text-zinc-100 hover:bg-white/10 hover:text-amber-300"
                    }`
                  }
                  data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
          <button
            className="md:hidden text-zinc-100 p-2"
            onClick={() => setMobileNav((v) => !v)}
            aria-label="Toggle menu"
            data-testid="marketing-mobile-nav-toggle"
          >
            {mobileNav ? <XIcon className="h-6 w-6"/> : <MenuIcon className="h-6 w-6"/>}
          </button>
        </div>
        {/* Mobile nav drawer · v2.6.9 batch CF · Flatten dropdowns inline
            so mobile users still see the Leadership / Drivers / Tourism
            cities entries. */}
        {mobileNav && (
          <div className="md:hidden bg-black/85 backdrop-blur-sm border-t border-amber-400/30" data-testid="marketing-mobile-nav">
            <nav className="px-5 py-2 space-y-1">
              {STATIC_NAV.map((n) => (
                <div key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setMobileNav(false)}
                    className={({ isActive }) =>
                      `block px-3 py-2.5 text-sm font-bold uppercase tracking-wide rounded ${
                        isActive ? "bg-amber-400 text-zinc-950" : "text-zinc-100 hover:bg-white/10"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                  {n.hasDropdown === "about" && (
                    <div className="pl-4 space-y-0.5">
                      {/* v2.7.65+E239 · Our Team replaces Leadership + Drivers. */}
                      <NavLink to="/our-team" onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ OUR TEAM</NavLink>
                      <NavLink to="/bison-taxi-commander" onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ BISON TAXI COMMANDER</NavLink>
                    </div>
                  )}
                  {n.hasDropdown === "tourism" && (
                    <div className="pl-4 space-y-0.5">
                      {(cfg?.tourism?.cities || []).filter((c) => c.enabled !== false).map((c) => (
                        <NavLink key={c.slug} to={`/tourism/${c.slug}`} onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ {(c.name || "").toUpperCase()}</NavLink>
                      ))}
                    </div>
                  )}
                  {/* v2.7.65+E134 · CCVAP dropdown mirrored inline on mobile */}
                  {n.hasDropdown === "ccvap" && (
                    <div className="pl-4 space-y-0.5">
                      <NavLink to="/voucher-program" onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ CCVAP PROGRAM INFO &amp; APPLICATION</NavLink>
                      <NavLink to="/my-vouchers"     onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ MY VOUCHERS</NavLink>
                    </div>
                  )}
                  {/* v2.7.65+E238 · Group Rides under Taxi Rates (mobile) */}
                  {n.hasDropdown === "rates" && (
                    <div className="pl-4 space-y-0.5">
                      <NavLink to="/group-rides" onClick={() => setMobileNav(false)} className="block px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-amber-300">↳ GROUP RIDES</NavLink>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* ─────────────────── MAIN CONTENT (75% centered) ─────────────────── */}
      <main className="w-full" data-testid="marketing-main">
        {children}
      </main>

      {/* v2.7.65+E106 · Katie AI Concierge — the public-facing chatbot.
          Replaces the legacy ChatbotWidget on every marketing page. Named
          after Steven Buller's wife; handles booking, ride status, tickets,
          and rate/coverage questions with strict admin-info safeguards. */}
      <KatieChatWidget />
      {/* Legacy ChatbotWidget retired in favor of Katie. Kept in the tree
          for a one-release deprecation window in case a stale route still
          references it. Set to null-render for now. */}
      {false && <ChatbotWidget />}

      {/* ─────────────────── FOOTER ───────────────────
          v2.6.9 batch CF · Yellow line above footer thinned to 1 px (was
          4 px) per spec. */}
      <footer
        className="border-t border-amber-400 mt-12 relative"
        style={
          g.footer_use_color === false && g.footer_bg_image_url
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.7)), url("${g.footer_bg_image_url}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundColor: g.footer_bg_color || "#0a0a0a",
              }
            : { backgroundColor: g.footer_bg_color || "#000000" }
        }
        data-testid="marketing-footer"
      >
        {/* v2.6.9 batch CK · Footer columns switched from a 4-col grid to a
            flex flex-wrap container with `justify-center`. With the grid
            layout, each column was equal-width and its inner content was
            left-aligned — that made the rightmost column ("Follow Us") read
            as if the footer skewed left because the social icons hugged the
            left edge of an over-wide column. Flex sizes each column to its
            actual content and centers the lot. */}
        <div className="max-w-7xl mx-auto px-5 py-10 flex flex-wrap justify-center gap-x-12 gap-y-8 text-center md:text-left">
          <div className="w-full sm:w-auto sm:max-w-[280px] flex flex-col items-center sm:items-start">
            <div className="flex items-center gap-3 mb-3">
              <img src={logo} alt="" className="h-10 w-10 object-contain"/>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold leading-none">{g.site_name?.split(" ")[0] || "BULLER"}</div>
                <div className="text-sm font-extrabold text-zinc-100">{g.site_name?.split(" ").slice(1).join(" ") || "Taxi Service"}</div>
              </div>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed max-w-[260px]">
              Professionally operated taxi service based in Weatherford, OK.
            </p>
          </div>

          <div className="w-full sm:w-auto sm:min-w-[160px] flex flex-col items-center sm:items-start">
            <h4 className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-3">QUICK LINKS</h4>
            <ul className="space-y-1.5 text-sm">
              {STATIC_NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-zinc-400 hover:text-amber-300 transition-colors uppercase tracking-wide text-xs font-bold" data-testid={`footer-link-${n.label.replace(/\s/g, "-").toLowerCase()}`}>{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* v2.6.9 batch CL · LEGAL column — Privacy / Terms / Refund / FAQ.
              Sits right next to Quick Links per spec. Same column structure
              (w-full sm:w-auto sm:min-w-[160px] flex-col centered on mobile,
              left-aligned at sm+) so it doesn't disturb the flex justify-center
              footer layout introduced in batch CK. */}
          <div className="w-full sm:w-auto sm:min-w-[160px] flex flex-col items-center sm:items-start" data-testid="footer-col-legal">
            <h4 className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-3">LEGAL</h4>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/legal/privacy" className="text-zinc-400 hover:text-amber-300 transition-colors uppercase tracking-wide text-xs font-bold" data-testid="footer-link-privacy">PRIVACY POLICY</Link></li>
              <li><Link to="/legal/terms"   className="text-zinc-400 hover:text-amber-300 transition-colors uppercase tracking-wide text-xs font-bold" data-testid="footer-link-terms">TERMS OF SERVICE</Link></li>
              <li><Link to="/legal/refund"  className="text-zinc-400 hover:text-amber-300 transition-colors uppercase tracking-wide text-xs font-bold" data-testid="footer-link-refund">REFUND POLICY</Link></li>
              <li><Link to="/legal/faq"     className="text-zinc-400 hover:text-amber-300 transition-colors uppercase tracking-wide text-xs font-bold" data-testid="footer-link-faq">FAQ</Link></li>
            </ul>
          </div>

          <div className="w-full sm:w-auto sm:min-w-[220px] flex flex-col items-center sm:items-start">
            <h4 className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-3">Contact</h4>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li className="flex items-start gap-2"><Phone className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/><a href={`tel:${phone.replace(/\D/g, "")}`} className="hover:text-amber-300">{phone}</a></li>
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/><a href={`mailto:${g.email}`} className="hover:text-amber-300">{g.email || "support@bullertaxi.com"}</a></li>
              {g.address && <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/><span>{g.address}</span></li>}
              <li className="flex items-start gap-2" data-testid="footer-hours">
                <Clock className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/>
                <span>
                  <span className="block text-[10px] uppercase tracking-widest text-amber-400 font-bold">Hours of Operation</span>
                  {hoursInfo ? `${hoursInfo.days_label}, ${hoursInfo.business_hours_label}` : "7 days a week, 8:00 AM – 3:00 AM CDT"}
                </span>
              </li>
            </ul>
          </div>

          <div className="w-full sm:w-auto flex flex-col items-center">
            <h4 className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-3">Follow Us</h4>
            <div className="flex items-center flex-wrap justify-center gap-2.5" data-testid="footer-socials">
              {/* v2.6.9 batch BS · Full-color brand SVGs + force absolute URLs.
                  v2.8+E307 · Facebook always present (official page default). */}
              {["facebook", "instagram", "twitter", "tiktok", "youtube"]
                .filter((key) => socialLinks[key])
                .map((key) => {
                  const Icon = brandIconFor(key);
                  const href = normalizeSocialUrl(socialLinks[key]);
                  if (!Icon) return null;
                  return (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-social-${key}`}
                      className="h-10 w-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-md transition-transform hover:scale-110"
                      aria-label={key}
                      title={key}
                    >
                      <Icon size={22}/>
                    </a>
                  );
                })}
            </div>
          </div>
        </div>

        {/* v2.6.9 batch BS/BY · Payment logos — centered, individual badge
            wrappers with 50%-opacity brand backgrounds (translucent), rounded
            edges. Sits exactly 30 px above the copyright. */}
        <div className="flex justify-center items-center mb-[30px]" data-testid="footer-payment-logos">
          <PaymentLogos size="md" translucent />
        </div>
        <div className="border-t border-white/10 px-5 py-4 text-center text-[11px] text-zinc-400" data-testid="footer-copyright">
          {g.footer_copyright || "© 2026 Buller Taxi Services, LLC. All Rights Reserved."}
        </div>

        {/* v2.7.65+E132 · PROUD MEMBER · Weatherford Chamber of Commerce
            Replaces the previous TAXIcommander + Bison AI + Taxi Commander GPS
            tri-logo strip per operator directive. Chamber logo links out to
            the chamber directory in a new tab. */}
        <div className="border-t border-white/10 px-5 pt-4 pb-5 bg-black/30" data-testid="footer-chamber-strip">
          <div
            className="text-center text-[10px] uppercase tracking-[0.4em] font-extrabold mb-2 text-amber-400"
            data-testid="footer-proud-member"
          >
            PROUD MEMBER
          </div>
          <div className="flex items-center justify-center">
            <a
              href="https://cityofweatherford.com/chamber-of-commerce/directory/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Weatherford Chamber of Commerce Directory (opens in a new tab)"
              className="inline-block hover:opacity-90 transition-opacity"
              data-testid="footer-chamber-link"
            >
              <img
                src="/assets/images/weatherford-chamber-logo.png"
                alt="Weatherford Chamber of Commerce"
                className="object-contain drop-shadow-[0_2px_10px_rgba(250,204,21,0.14)]"
                style={{ width: "220px", maxWidth: "220px", height: "auto" }}
                data-testid="footer-chamber-logo"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}


/**
 * v2.6.9 batch CF · NavDropdown — hover-and-click capable dropdown for the
 * desktop header nav. Used by:
 *   • About → Leadership → Drivers (nested visually with indent)
 *   • Tourism → all enabled cities
 *
 * Opens on hover (desktop UX) and on click (a11y / touch). Closes on
 * route change and outside-click. All link labels render in ALL CAPS per
 * spec. Top-level "About" / "Tourism" link itself remains clickable so
 * the user can still land on the section's overview page.
 */
function NavDropdown({ parent, items, cfgTitle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const hasItems = Array.isArray(items) && items.length > 0;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`nav-dropdown-${parent.hasDropdown}`}
    >
      <div className="flex items-stretch">
        <NavLink
          to={parent.to}
          end={parent.to === "/"}
          className={({ isActive }) =>
            `pl-3 pr-1 py-2 text-sm font-extrabold uppercase tracking-wide transition-colors rounded-l ${
              isActive ? "bg-amber-400 text-zinc-950" : "text-zinc-100 hover:bg-white/10 hover:text-amber-300"
            }`
          }
          data-testid={`nav-${parent.to.replace("/", "")}`}
        >
          {parent.label}
        </NavLink>
        {hasItems && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open submenu"
            aria-expanded={open}
            className={`pr-2 pl-1 py-2 text-amber-300 hover:text-amber-200 rounded-r transition-colors ${open ? "bg-white/10" : ""}`}
            data-testid={`nav-dropdown-${parent.hasDropdown}-toggle`}
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}/>
          </button>
        )}
      </div>

      {hasItems && open && (
        <div
          className="absolute top-full left-0 mt-1 min-w-[220px] bg-zinc-950/95 backdrop-blur-md border border-amber-400/40 rounded-lg shadow-xl shadow-black/60 py-1.5 z-50"
          data-testid={`nav-dropdown-${parent.hasDropdown}-panel`}
        >
          {cfgTitle && (
            <div className="px-4 py-1.5 text-[9px] uppercase tracking-[0.3em] text-amber-400/70 font-bold border-b border-white/10 mb-1">{cfgTitle}</div>
          )}
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-2 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-amber-400 text-zinc-950" : "text-zinc-200 hover:bg-amber-400/15 hover:text-amber-300"
                } ${it.indent ? "pl-7" : ""}`
              }
              data-testid={`nav-dropdown-${parent.hasDropdown}-item-${it.to.replace(/\//g, "-")}`}
            >
              {it.indent ? `↳ ${it.label}` : it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
